/**
 * isiZulu: Signing in and getting started
 *
 * What this file covers: Sign in, sign up, password reset, two-step sign-in, email verification, the data
 * processing agreement and the first-run onboarding.
 *
 * Tone: Warm, calm and reassuring, in very plain words: this is a business owner's first impression. Security
 * messages stay exact (a wrong code is wrong, an expired link is expired). The agreement text is legal:
 * translate it faithfully, never shorten or soften it.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const signIn: Catalog = {
  'auth.common.password': 'Iphasiwedi',
  'auth.common.codeMismatch': 'Lelo khodi alihambisani.',
  'auth.common.backToSignIn': 'Buyela ekungeneni',
  'auth.login.newHere': 'Umusha lapha?',
  'auth.login.createAccount': 'Dala i-akhawunti',
  'auth.login.forgotPassword': 'Ukhohlwe iphasiwedi?',
  'auth.login.failed': 'Ukungena kwehlulekile.',
  'auth.login.twofa.form.code': 'Ikhodi yokuqinisekisa',
  'auth.login.twofa.verify': 'Qinisekisa',
  'auth.signup.title': 'Dala i-akhawunti yakho',
  'auth.signup.haveAccount': 'Usunayo i-akhawunti?',
  'auth.signup.form.orgName': 'Igama lenhlangano',
  'auth.signup.form.workEmail': 'I-imeyili yomsebenzi',
  'auth.signup.submit': 'Dala i-akhawunti',
  'auth.signup.failed': 'Ukubhalisa kwehlulekile.',
  'auth.forgot.title': 'Setha kabusha iphasiwedi yakho',
  'auth.forgot.submit': 'Thumela isixhumanisi sokusetha kabusha',
  'auth.forgot.sent.title': 'Bheka ibhokisi lakho lokungenayo',
  'auth.reset.title': 'Khetha iphasiwedi entsha',
  'auth.reset.form.newPassword': 'Iphasiwedi entsha',
  'auth.reset.form.confirmPassword': 'Qinisekisa iphasiwedi',
  'auth.reset.submit': 'Setha kabusha iphasiwedi',
  'auth.reset.errors.tooShort': 'Iphasiwedi kufanele ibe nezinhlamvu ezingu-8 okungenani.',
  'auth.reset.errors.mismatch': 'Amaphasiwedi awahambisani.',
  'auth.reset.failed': 'Ayikwazanga ukusetha kabusha iphasiwedi.',
  'auth.reset.toast.doneTitle': 'Iphasiwedi isethwe kabusha',
  'auth.verify.title': 'Qinisekisa i-imeyili yakho',
  'auth.verify.form.code': 'Ikhodi enezinhlamvu ezingu-6',
  'auth.verify.submit': 'Qinisekisa i-imeyili',
  'auth.verify.resend': 'Thumela ikhodi futhi',
  'auth.verify.backToDashboard': 'Buyela kwideshibhodi',
  'auth.verify.toast.done': 'I-imeyili yakho iqinisekisiwe.',
  'auth.verify.toast.resentTitle': 'Ikhodi ithunyelwe',
  'auth.verify.toast.resendFailed': 'Ayikwazanga ukuthumela futhi.',
  'auth.verify.toast.resendFailedTitle': 'Ukuthumela futhi kwehlulekile',
  'auth.mfa.title': 'Ukungena ngezigaba ezimbili',
  'auth.mfa.goToSignIn': 'Iya ekungeneni',
  'onboarding.title': 'Wamukelekile ku-RepletoryReach',
  'onboarding.skipSetup': 'Yeqa ukusetha',
  'onboarding.steps.verify.label': 'Qinisekisa i-imeyili',
  'onboarding.steps.verify.title': 'Qinisekisa i-imeyili yakho',
  'onboarding.steps.verify.form.code': 'Ikhodi yokuqinisekisa',
  'onboarding.steps.verify.resend': 'Thumela ikhodi futhi',
  'onboarding.steps.verify.skip': 'Yeqa okwamanje',
  'onboarding.steps.verify.confirm': 'Qinisekisa',
  'onboarding.steps.region.label': 'Isifunda',
  'onboarding.steps.region.title': 'Usebenza kuphi?',
  'onboarding.steps.region.form.country': 'Izwe',
  'onboarding.steps.region.country.za': 'INingizimu Afrika (ZAR)',
  'onboarding.steps.region.country.ng': 'INigeria (NGN)',
  'onboarding.steps.region.country.ke': 'IKenya (KES)',
  'onboarding.steps.name.label': 'Igama lakho',
  'onboarding.steps.name.title': 'Sikubize ngani?',
  'onboarding.steps.name.form.displayName': 'Igama elibonakalayo',
  'onboarding.steps.number.label': 'Inombolo ye-WhatsApp',
  'onboarding.steps.number.title': 'Namathisela inombolo ye-WhatsApp',
  'onboarding.steps.number.openNumbers': 'Vula Izinombolo',
  'onboarding.steps.done.label': 'Kuqediwe',
  'onboarding.steps.done.title': 'Usukulungele',
  'onboarding.steps.done.goToDashboard': 'Iya kwideshibhodi',
  'onboarding.toast.emailVerified': 'I-imeyili iqinisekisiwe.',
  'onboarding.toast.emailVerifiedTitle': 'Ukungeniswa',
  'onboarding.toast.codeMismatch': 'Ikhodi ayihambisani.',
  'onboarding.toast.codeMismatchTitle': 'Ukuqinisekiswa',
  'onboarding.toast.saveFailed': 'Ayikwazanga ukulondoloza.',
  'onboarding.toast.profileFailedTitle': 'Iphrofayela',
  'onboarding.stepOf': 'Isinyathelo {{step}} kwezingu-{{total}}',
  'onboarding.stepLabel': 'Isinyathelo',
  'auth.social.or': 'noma',
  'auth.social.continueWith': 'Qhubeka nge-{{provider}}',
  'auth.social.provider': 'umhlinzeki wakho',
  'auth.social.hint': 'Sithola kuphela igama lakho nekheli lakho le-imeyili, futhi kuphela emva kokuba uvumile.',
  'auth.callback.signingIn': 'Siyakungenisa…',
  'auth.callback.failedTitle': 'Lokho kungena akuphelelanga',
  'auth.callback.failedLead': 'Akukho okushintshile ku-akhawunti yakho. Zama futhi, noma ungene ngekheli lakho le-imeyili nephasiwedi.',
  'auth.callback.backToSignIn': 'Buyela ekungeneni'
};

export default signIn;
